// FILE: src/services/performanceHistoryService.js
//
// Phase B — reads from the `learner_session_summary` view (migration 019) and
// produces the data shapes the PerformanceHistory page renders:
//   • sessions          — normalized list of session rows, newest first
//   • summaryByExam     — { [examId]: { count, bestPct, avgPct, weakPhases[] } }
//   • availableExams    — examIds the learner has tracked data for
//     (powers the cert filter chips — chips only show for certs with data)
//
// Source of truth: question_attempts joined to questions.category, surfaced
// through the view. NOT exam_sessions.question_summary. See
// PLAN-phase-a-design.md §6.9.2.
//
// RLS handles per-learner filtering: the view has security_invoker=on and the
// underlying exam_sessions / question_attempts policies (migration 018) limit
// the result set to the calling user's rows. We don't pass user_id here.

import { supabase } from './supabaseClient';

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Loads all sessions for the signed-in learner, newest first. Returns an
 * empty array on RLS-empty (no data) or error — never throws.
 *
 * @returns {Promise<LearnerSessionRow[]>}
 */
export async function loadLearnerSessions() {
  try {
    const { data, error } = await supabase
      .from('learner_session_summary')
      .select('*')
      .order('started_at', { ascending: false });

    if (error) {
      console.warn('[performanceHistoryService] load failed:', error.message);
      return [];
    }
    if (!Array.isArray(data)) return [];
    return data.map(normalizeRow);
  } catch (err) {
    console.warn('[performanceHistoryService] unexpected error:', err?.message || err);
    return [];
  }
}

/**
 * Bundles the dashboard shape the PerformanceHistory page needs in one call:
 *   - sessions          (already normalized)
 *   - summaryByExam     (per-cert aggregates derived in JS)
 *   - availableExams    (examIds with at least one session)
 *
 * Pure derivation from `loadLearnerSessions()` — no extra round trip. Exported
 * for the page to call once on mount.
 *
 * @returns {Promise<{ sessions: LearnerSessionRow[], summaryByExam: object, availableExams: string[] }>}
 */
export async function loadPerformanceHistory() {
  const sessions       = await loadLearnerSessions();
  const summaryByExam  = computeSummaryByExam(sessions);
  const availableExams = Object.keys(summaryByExam);
  return { sessions, summaryByExam, availableExams };
}

// ── Pure derivations (exported for testing) ─────────────────────────────────

/**
 * Per-exam aggregates: count, best %, avg %, top-3 weak phases.
 *
 * Weak phases are computed across the learner's last N sessions for the exam
 * (default 5). A phase counts as "weak" if its weighted accuracy < 70% and at
 * least 5 questions have been seen.
 *
 * @param {LearnerSessionRow[]} sessions
 * @param {number} [recentWindow=5]
 * @returns {object} { [examId]: SummaryRow }
 */
export function computeSummaryByExam(sessions, recentWindow = 5) {
  const out = {};
  if (!Array.isArray(sessions) || sessions.length === 0) return out;

  // Group by exam, newest first.
  const grouped = {};
  for (const s of sessions) {
    if (!s.examId) continue;
    if (!grouped[s.examId]) grouped[s.examId] = [];
    grouped[s.examId].push(s);
  }

  for (const examId of Object.keys(grouped)) {
    const list = grouped[examId];
    const count   = list.length;
    const pcts    = list.map((s) => s.scorePercentage).filter((n) => typeof n === 'number');
    const bestPct = pcts.length ? Math.max(...pcts) : null;
    const avgPct  = pcts.length
      ? Math.round((pcts.reduce((a, b) => a + b, 0) / pcts.length) * 10) / 10
      : null;
    const weakPhases = computeWeakPhases(list.slice(0, recentWindow));
    const lastAttemptAt = list[0]?.startedAt || null;

    out[examId] = {
      examId,
      count,
      bestPct,
      avgPct,
      weakPhases,
      lastAttemptAt,
    };
  }

  return out;
}

/**
 * Weighted weak-phase calc. Aggregates phase_breakdown across the given
 * sessions and returns up to 3 weakest phases with at least 5 total questions
 * seen and accuracy < 70%.
 *
 * Exported for unit tests.
 *
 * @param {LearnerSessionRow[]} recentSessions
 * @returns {Array<{ phase: string, accuracy: number, total: number, correct: number }>}
 */
export function computeWeakPhases(recentSessions) {
  if (!Array.isArray(recentSessions) || recentSessions.length === 0) return [];

  const acc = {}; // phase -> { total, correct }
  for (const s of recentSessions) {
    const pb = s.phaseBreakdown || {};
    for (const phase of Object.keys(pb)) {
      const entry = pb[phase] || {};
      const total   = Number(entry.total)   || 0;
      const correct = Number(entry.correct) || 0;
      if (!acc[phase]) acc[phase] = { total: 0, correct: 0 };
      acc[phase].total   += total;
      acc[phase].correct += correct;
    }
  }

  const rows = Object.keys(acc).map((phase) => {
    const { total, correct } = acc[phase];
    const accuracy = total > 0 ? Math.round((100.0 * correct) / total) : 0;
    return { phase, accuracy, total, correct };
  });

  return rows
    .filter((r) => r.total >= 5 && r.accuracy < 70)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, 3);
}

/**
 * Normalize the snake_case view row into the camelCase shape the UI consumes.
 *
 * @param {object} row — raw from PostgREST
 * @returns {LearnerSessionRow}
 */
export function normalizeRow(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    sessionId:        row.session_id,
    userId:           row.user_id,
    examId:           row.exam_id,
    mode:             row.mode,
    startedAt:        row.started_at,
    endedAt:          row.ended_at,
    completionReason: row.completion_reason,
    scorePercentage:  typeof row.score_percentage === 'number' ? row.score_percentage : null,
    scoreCorrect:     typeof row.score_correct    === 'number' ? row.score_correct    : 0,
    scoreIncorrect:   typeof row.score_incorrect  === 'number' ? row.score_incorrect  : 0,
    scoreSkipped:     typeof row.score_skipped    === 'number' ? row.score_skipped    : 0,
    scoreUnanswered:  typeof row.score_unanswered === 'number' ? row.score_unanswered : 0,
    totalQuestions:   typeof row.total_questions  === 'number' ? row.total_questions  : 0,
    durationSeconds:  typeof row.duration_seconds === 'number' ? row.duration_seconds : null,
    source:           row.source,
    phaseBreakdown:   row.phase_breakdown || null,
    secondaryBreakdown: row.secondary_breakdown || null,
  };
}

// ── Types (JSDoc) ────────────────────────────────────────────────────────────
//
// LearnerSessionRow:
//   {
//     sessionId, userId, examId, mode,
//     startedAt, endedAt, completionReason,
//     scorePercentage, scoreCorrect, scoreIncorrect, scoreSkipped,
//     scoreUnanswered, totalQuestions, durationSeconds, source,
//     phaseBreakdown: { [phase]: { total, correct, pct } } | null
//   }
//
// SummaryRow:
//   {
//     examId, count, bestPct, avgPct, lastAttemptAt,
//     weakPhases: Array<{ phase, accuracy, total, correct }>
//   }
